import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { caseFileApi, screeningApi, type CaseEntry } from '../../api/case-file.api.js';
import { useAuth } from '../../auth/useAuth.js';

import { CustomerKycReports } from './CustomerKycReports.js';
import { APP_LOCALE } from '../../utils/locale.js';

/**
 * Everything that has happened to this customer, on one timeline.
 *
 * Seven tracker items asked for different panels here — an audit trail, an
 * evaluation log, screening history, notes, a risk rating, source of funds,
 * document expiry. They are one list, deliberately: a periodic review reads
 * what happened in order, and an evaluation that cannot be seen next to the
 * screening it followed is half a record.
 *
 * The entries the platform writes for itself are marked as such. An analyst can
 * add their own reasoning but cannot write an action, because a trail somebody
 * can author by hand is not a trail.
 */

/** What an analyst may add, and what each is for. */
const WRITABLE_TYPES: ReadonlyArray<{ value: string; label: string; hint: string }> = [
  { value: 'NOTE', label: 'Note', hint: 'Commentary on this client or a decision taken.' },
  {
    value: 'EVALUATION',
    label: 'Evaluation',
    hint: 'A KYC review and its outcome — periodic or triggered.',
  },
  {
    value: 'SCREENING',
    label: 'Screening',
    hint: 'A sanctions or adverse-media check, and what it found.',
  },
  { value: 'RISK', label: 'Risk rating', hint: 'A rating decision and why it was set there.' },
  {
    value: 'SOURCE',
    label: 'Source of funds / wealth',
    hint: 'What the client declared, and when it was collected.',
  },
  { value: 'DOCUMENT', label: 'Document', hint: 'A document received, its status or expiry.' },
];

const TYPE_TONE: Readonly<
  Record<string, 'default' | 'primary' | 'success' | 'warning' | 'info'>
> = {
  ACTION: 'default',
  EVALUATION: 'primary',
  NOTE: 'info',
  SCREENING: 'warning',
  RISK: 'warning',
  SOURCE: 'success',
  DOCUMENT: 'success',
};

export function CustomerCaseFileTab({ customerId, isBusiness }: {
  customerId: string;
  isBusiness: boolean;
}): JSX.Element {
  const { can } = useAuth();
  // The case file is read by every role that sees the customer; AML / MLRO writes it (DEV-67).
  const canWrite = can('kyc.decide');
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [entryType, setEntryType] = useState('NOTE');
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState('');
  const [reviewKind, setReviewKind] = useState<'PERIODIC' | 'TRIGGERED'>('PERIODIC');
  const [failure, setFailure] = useState<string | null>(null);

  const entries = useQuery({
    queryKey: ['case-file', customerId],
    queryFn: () => caseFileApi.forCustomer(customerId),
    refetchInterval: (query) => query.state.data?.find((e) => e.entryType === 'SCREENING' && typeof e.data?.['screeningId'] === 'string')
      ?.data?.['pending'] === true ? 10_000 : false,
  });

  /**
   * Run a fresh screening.
   *
   * Sanctions lists change weekly, so a check from eighteen months ago is not
   * an answer to "are they on a list now". The result files itself.
   */
  const rescreen = useMutation({
    mutationFn: () => screeningApi.rescreen(customerId),
    onSuccess: async () => {
      setFailure(null);
      await queryClient.invalidateQueries({ queryKey: ['case-file', customerId] });
    },
    onError: (err: Error) => setFailure(err.message),
  });

  const add = useMutation({
    mutationFn: () =>
      caseFileApi.add(customerId, {
        entryType,
        summary: summary.trim(),
        ...(body.trim() !== '' ? { body: body.trim() } : {}),
        ...(entryType === 'EVALUATION' ? { reviewKind } : {}),
      }),
    onSuccess: async () => {
      setAdding(false);
      setSummary('');
      setBody('');
      setFailure(null);
      await queryClient.invalidateQueries({ queryKey: ['case-file', customerId] });
      // An evaluation moves the review date shown on the client header.
      await queryClient.invalidateQueries({ queryKey: ['customer', customerId] });
    },
    onError: (err: Error) => setFailure(err.message),
  });

  const rows: CaseEntry[] = entries.data ?? [];
  // The most recent screening, stated at the top. Buried in a timeline it
  // answers "when did we last check" only for somebody willing to scroll.
  const lastScreening = rows.find((e) => e.entryType === 'SCREENING' && typeof e.data?.['screeningId'] === 'string')
    ?? rows.find((e) => e.entryType === 'SCREENING');
  const screeningPending = lastScreening?.data?.['pending'] === true;
  const lastSource = rows.find((e) => e.entryType === 'SOURCE');

  return (
    <Stack spacing={2}>
      {canWrite ? (
        <CustomerKycReports customerId={customerId} isBusiness={isBusiness} />
      ) : null}
      {entries.isLoading ? (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">Loading screening attempts and case history…</Typography>
        </Stack>
      ) : null}
      <Stack direction="row" spacing={2} alignItems="center">
        {canWrite ? <><Button variant="contained" onClick={() => setAdding(true)}>
          Add an entry
        </Button>
        <Button
          variant="outlined"
          disabled={entries.isLoading || entries.isError || rescreen.isPending || screeningPending}
          onClick={() => rescreen.mutate()}
        >
          {rescreen.isPending || screeningPending ? 'Screening in progress…' : 'Re-screen now'}
        </Button></> : null}
        <Typography variant="body2" color="text.secondary">
          {rows.length === 0
            ? 'Nothing recorded yet.'
            : `${String(rows.length)} ${rows.length === 1 ? 'entry' : 'entries'}, newest first.`}
        </Typography>
      </Stack>

      {failure !== null && !adding ? <Alert severity="error">{failure}</Alert> : null}

      {entries.isLoading || entries.isError ? null : lastScreening === undefined ? (
        <Alert severity="warning">
          No screening attempt is recorded in this case file. Existing provider reports can still be opened above.
        </Alert>
      ) : (
        <Alert severity={lastScreening.data?.['clear'] === true ? 'success' : 'warning'}>
          <strong>{screeningPending ? 'New screening requested' : 'Latest screening attempt'} {new Date(lastScreening.occurredAt).toLocaleString(APP_LOCALE)}</strong>
          {' — '}
          {String(lastScreening.data?.['result'] ?? lastScreening.summary)}
          {screeningPending ? ' · This page updates automatically when the provider check completes.' : ''}
          {!screeningPending && lastScreening.body ? ` · ${lastScreening.body}` : ''}
          {Array.isArray(lastScreening.data?.['matchedLists']) &&
          (lastScreening.data['matchedLists'] as unknown[]).length > 0
            ? ` · ${(lastScreening.data['matchedLists'] as string[]).join(', ')}`
            : ''}
        </Alert>
      )}

      {/* Source of funds and wealth, stated rather than left in the timeline.
          They answer different questions — where this money came from, and how
          the client accumulated what they have — and standard CDD needs the
          first while EDD needs both. A reviewer should not have to scroll to
          find out whether either is on file. */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Source of funds and wealth
        </Typography>
        {lastSource === undefined ? (
          <Typography variant="body2" color="text.secondary">
            Nothing declared on file. Source of funds is expected for standard due diligence;
            source of wealth as well for a high-risk client or anything under EDD.
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            <Typography variant="body2">
              <strong>Declared:</strong> {lastSource.summary}
            </Typography>
            {lastSource.body !== null && lastSource.body !== '' ? (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {lastSource.body}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              Collected {new Date(lastSource.occurredAt).toLocaleDateString(APP_LOCALE)} ·{' '}
              <code>{lastSource.actorId}</code>
            </Typography>
          </Stack>
        )}
      </Box>

      {entries.isError ? (
        <Alert severity="error">
          The case file could not be read. Nothing has been lost — try again.
        </Alert>
      ) : null}

      {rows.length === 0 && !entries.isError && !entries.isLoading ? (
        <Alert severity="info">
          Nothing has been recorded against this client yet. Decisions taken from here on are
          filed automatically; anything you conclude, add yourself.
        </Alert>
      ) : null}

      <Stack spacing={1.5}>
        {rows.map((e) => (
          <Box
            key={e.id}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderLeft: '3px solid',
              borderLeftColor: e.entryType === 'ACTION' ? 'divider' : 'primary.main',
              borderRadius: 1,
              p: 2,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
              <Chip
                size="small"
                label={e.entryType}
                color={TYPE_TONE[e.entryType] ?? 'default'}
                variant={e.entryType === 'ACTION' ? 'outlined' : 'filled'}
              />
              <Typography variant="body2" color="text.secondary">
                {new Date(e.occurredAt).toLocaleString(APP_LOCALE)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                · <code>{e.actorId}</code>
              </Typography>
              {e.subjectType !== null ? (
                <Chip size="small" variant="outlined" label={e.subjectType.replace('_', ' ')} />
              ) : null}
            </Stack>
            <Typography variant="body1">{e.summary}</Typography>
            {e.body !== null && e.body !== '' ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {e.body}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Stack>

      <Dialog open={adding} onClose={() => setAdding(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add to the case file</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {failure !== null ? <Alert severity="error">{failure}</Alert> : null}
            <TextField
              select
              label="What kind of entry"
              value={entryType}
              onChange={(event) => setEntryType(event.target.value)}
              helperText={WRITABLE_TYPES.find((t) => t.value === entryType)?.hint}
            >
              {WRITABLE_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            {entryType === 'EVALUATION' ? (
              <TextField
                select
                label="Kind of review"
                value={reviewKind}
                onChange={(event) => setReviewKind(event.target.value as 'PERIODIC' | 'TRIGGERED')}
                helperText="Recording an evaluation schedules the next one from the client's risk band."
              >
                <MenuItem value="PERIODIC">Periodic — the scheduled cycle</MenuItem>
                <MenuItem value="TRIGGERED">Triggered — prompted by something</MenuItem>
              </TextField>
            ) : null}
            {/* A review is the moment to check that what is on file is still
                true. Source of funds collected at onboarding and never revisited
                describes a client who no longer exists. */}
            {entryType === 'EVALUATION' ? (
              <Alert severity={lastSource === undefined ? 'warning' : 'info'}>
                {lastSource === undefined
                  ? 'Nothing is on file for source of funds. Collect it as part of this review and record it as its own entry.'
                  : `Source of funds was last recorded ${new Date(
                      lastSource.occurredAt,
                    ).toLocaleDateString(APP_LOCALE)}. Reconfirm it with the client, and add a new entry if anything has changed.`}
              </Alert>
            ) : null}
            <TextField
              label="One line"
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
              helperText="What this entry says, read on its own in a timeline."
            />
            <TextField
              label="Detail (optional)"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              multiline
              minRows={4}
              helperText="Your reasoning. This is what a reviewer reads next year."
            />
            <Alert severity="info">
              Entries cannot be edited or removed once added. To correct one, add another that
              says so.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={summary.trim().length < 3 || add.isPending}
            onClick={() => add.mutate()}
          >
            {add.isPending ? 'Recording…' : 'Record it'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
